import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronRight, Trash2, Plus, Package, Wrench, Boxes } from 'lucide-react';
import BoqRowGrouped from './BoqRowGrouped';
import { HOUSE_SECTIONS } from '../../constants/houseSections';
import { WORK_ITEM_SUGGESTIONS } from '../../constants/boq';

// Single accent system for unit areas — dark slate gradient header band with a flamingo medallion.
const AREA_ACCENT = {
    gradient: 'from-slate-800 to-slate-700',
    medallionBg: 'bg-flamingo-500/20',
    medallionBorder: 'border-flamingo-400/40',
    medallionText: 'text-flamingo-200',
    eyebrow: 'text-flamingo-300',
    chipText: 'text-flamingo-700',
    chipBg: 'bg-flamingo-50',
    chipBorder: 'border-flamingo-200',
};

const StatTile = ({ label, value }) => (
    <div className="px-3 py-2 rounded-xl border border-flamingo-400/30 bg-flamingo-500/10 min-w-[100px]">
        <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] text-slate-300 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-flamingo-400" />
            {label}
        </p>
        <p className="text-xl font-semibold text-flamingo-200 tabular-nums leading-tight mt-0.5">{value}</p>
    </div>
);

const PackageIcon = () => <Package size={22} className="text-flamingo-600" aria-hidden />;

// Collision-proof client-side id for new rows / work items / unit areas.
const newClientId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `cid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

// v3_58: a BOQ row is just a free-text material + quantity + unit of measure. No category chain,
// no price, no credits.
const makeEmptyMaterialRow = () => ({
    id: newClientId(),
    interiorDesignerBoqId: null,
    materialName: '',
    quantity: '',
    unit: '',
    notes: '',
});

// v3_58: a work item is a plain scope name within a unit area (e.g. TV Unit, False Ceiling).
// It carries NO execution package — the BOQ is a materials list, not an execution plan.
const makeEmptyWorkItem = () => ({
    id: newClientId(),
    boqWorkItemId: null,
    workItemName: '',
    isCollapsed: false,
    items: [makeEmptyMaterialRow()],
});

const AreaWorkItemBoqTable = ({
    unitAreas,
    setUnitAreas,
    onTrackDeletedUnitArea,
    onTrackDeletedWorkItem,
    onTrackDeletedMaterial,
}) => {
    const [splashDismissed, setSplashDismissed] = useState(false);

    const areaNameOptions = useMemo(() => HOUSE_SECTIONS.map((s) => ({ label: s, value: s })), []);

    // --- Unit Area handlers ---
    const addUnitArea = () => {
        setUnitAreas((prev) => [
            ...prev,
            { id: newClientId(), boqUnitAreaId: null, areaName: '', isCollapsed: false, workItems: [makeEmptyWorkItem()] },
        ]);
    };
    const toggleAreaCollapse = (areaIdx) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            next[areaIdx] = { ...next[areaIdx], isCollapsed: !next[areaIdx].isCollapsed };
            return next;
        });
    };
    const updateAreaName = (areaIdx, value) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            next[areaIdx] = { ...next[areaIdx], areaName: value };
            return next;
        });
    };
    const deleteUnitArea = (areaIdx) => {
        const area = unitAreas[areaIdx];
        if (!area) return;
        const label = area.areaName || 'this unit area';
        if (!window.confirm(`Remove "${label}" and all its work items?`)) return;
        if (area.boqUnitAreaId && onTrackDeletedUnitArea) onTrackDeletedUnitArea(area.boqUnitAreaId);
        if (onTrackDeletedWorkItem && Array.isArray(area.workItems)) {
            area.workItems.forEach((wi) => {
                if (wi.boqWorkItemId) onTrackDeletedWorkItem(wi.boqWorkItemId);
                if (onTrackDeletedMaterial && Array.isArray(wi.items)) {
                    wi.items.forEach((it) => {
                        const boqId = Number(it.interiorDesignerBoqId);
                        if (Number.isFinite(boqId) && boqId > 0) onTrackDeletedMaterial(boqId);
                    });
                }
            });
        }
        setUnitAreas((prev) => {
            const next = prev.filter((_, i) => i !== areaIdx);
            return next.length === 0
                ? [{ id: newClientId(), boqUnitAreaId: null, areaName: '', isCollapsed: false, workItems: [makeEmptyWorkItem()] }]
                : next;
        });
    };

    // --- Work Item handlers ---
    const addWorkItem = (areaIdx) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems, makeEmptyWorkItem()] };
            next[areaIdx] = area;
            return next;
        });
    };
    const toggleWorkItemCollapse = (areaIdx, wiIdx) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems] };
            area.workItems[wiIdx] = { ...area.workItems[wiIdx], isCollapsed: !area.workItems[wiIdx].isCollapsed };
            next[areaIdx] = area;
            return next;
        });
    };
    const updateWorkItemName = (areaIdx, wiIdx, value) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems] };
            area.workItems[wiIdx] = { ...area.workItems[wiIdx], workItemName: value };
            next[areaIdx] = area;
            return next;
        });
    };
    const deleteWorkItem = (areaIdx, wiIdx) => {
        const area = unitAreas[areaIdx];
        const wi = area?.workItems?.[wiIdx];
        if (!wi) return;
        const label = wi.workItemName || 'this work item';
        if (!window.confirm(`Remove "${label}" and all its material rows?`)) return;
        if (wi.boqWorkItemId && onTrackDeletedWorkItem) onTrackDeletedWorkItem(wi.boqWorkItemId);
        if (onTrackDeletedMaterial && Array.isArray(wi.items)) {
            wi.items.forEach((it) => {
                const boqId = Number(it.interiorDesignerBoqId);
                if (Number.isFinite(boqId) && boqId > 0) onTrackDeletedMaterial(boqId);
            });
        }
        setUnitAreas((prev) => {
            const next = [...prev];
            const a = { ...next[areaIdx], workItems: next[areaIdx].workItems.filter((_, i) => i !== wiIdx) };
            if (a.workItems.length === 0) a.workItems = [makeEmptyWorkItem()];
            next[areaIdx] = a;
            return next;
        });
    };

    // --- Material row handlers ---
    const addMaterialRow = useCallback((areaIdx, wiIdx) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems] };
            const wi = { ...area.workItems[wiIdx], items: [...area.workItems[wiIdx].items], isCollapsed: false };
            wi.items.push(makeEmptyMaterialRow());
            area.workItems[wiIdx] = wi;
            next[areaIdx] = area;
            return next;
        });
    }, [setUnitAreas]);

    // updateRow(sectionIndex=wiIdx, itemIdx, updates) — bound per area.
    const makeUpdateItem = useCallback((areaIdx) => (wiIdx, itemIdx, updates) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems] };
            const wi = { ...area.workItems[wiIdx], items: [...area.workItems[wiIdx].items] };
            wi.items[itemIdx] = { ...wi.items[itemIdx], ...updates };
            area.workItems[wiIdx] = wi;
            next[areaIdx] = area;
            return next;
        });
    }, [setUnitAreas]);

    const makeDeleteItem = useCallback((areaIdx) => (wiIdx, itemIdx) => {
        setUnitAreas((prev) => {
            const next = [...prev];
            const area = { ...next[areaIdx], workItems: [...next[areaIdx].workItems] };
            const wi = { ...area.workItems[wiIdx], items: [...area.workItems[wiIdx].items] };
            const removed = wi.items[itemIdx];
            if (removed) {
                const boqId = Number(removed.interiorDesignerBoqId);
                if (Number.isFinite(boqId) && boqId > 0 && onTrackDeletedMaterial) onTrackDeletedMaterial(boqId);
            }
            wi.items.splice(itemIdx, 1);
            if (wi.items.length === 0) wi.items = [makeEmptyMaterialRow()];
            area.workItems[wiIdx] = wi;
            next[areaIdx] = area;
            return next;
        });
    }, [setUnitAreas, onTrackDeletedMaterial]);

    // Summary tiles + empty-splash detection
    const totalUnitAreas = unitAreas.length;
    const totalWorkItems = unitAreas.reduce((acc, a) => acc + (a.workItems?.length || 0), 0);
    const totalMaterialRows = unitAreas.reduce(
        (acc, a) => acc + (a.workItems?.reduce((wAcc, w) => wAcc + (w.items?.length || 0), 0) || 0), 0);
    const isEmptySurface =
        !splashDismissed &&
        (unitAreas.length === 0 ||
            (unitAreas.length === 1 &&
                !unitAreas[0].areaName &&
                unitAreas[0].workItems?.length === 1 &&
                !unitAreas[0].workItems[0].workItemName &&
                (unitAreas[0].workItems[0].items?.length || 0) <= 1 &&
                !unitAreas[0].workItems[0].items?.[0]?.materialName));

    return (
        <div className="flex flex-col gap-5 p-3 lg:p-5 bg-gradient-to-b from-slate-50/60 via-white to-slate-50/40 min-h-full">
            <header className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700">
                <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_15%_20%,white,transparent_55%)] pointer-events-none" />
                <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:14px_14px] pointer-events-none" />
                <div className="relative px-5 lg:px-6 py-5 flex flex-wrap items-center justify-between gap-5">
                    <div className="flex items-center gap-3.5">
                        <div className="w-12 h-12 rounded-2xl bg-flamingo-500/20 border border-flamingo-500/30 flex items-center justify-center shadow-inner">
                            <Boxes size={22} className="text-flamingo-300" />
                        </div>
                        <div>
                            <p className="text-[10px] text-flamingo-300 uppercase tracking-[0.16em] font-semibold leading-none">Bill of Quantities</p>
                            <h2 className="text-lg sm:text-xl font-semibold text-white mt-1 tracking-tight">Materials &amp; quantities</h2>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-stretch gap-2">
                        <StatTile label="Unit areas" value={totalUnitAreas} />
                        <StatTile label="Work items" value={totalWorkItems} />
                        <StatTile label="Material rows" value={totalMaterialRows} />
                    </div>
                </div>
            </header>

            {isEmptySurface && (
                <div className="relative overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                    <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_50%_0%,#fef3c7,transparent_60%)] pointer-events-none" />
                    <div className="relative flex flex-col items-center gap-3 max-w-md mx-auto">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-flamingo-100 to-flamingo-200 border border-flamingo-200 flex items-center justify-center shadow-sm">
                            <PackageIcon />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-slate-900">Start your BOQ</h3>
                            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                                Add a <span className="font-medium text-slate-700">unit area</span> (e.g. Hall, Kitchen),
                                give it <span className="font-medium text-slate-700">work items</span> (e.g. TV Unit, Wardrobe),
                                then list the materials + quantities that go into each.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => { if (unitAreas.length === 0) addUnitArea(); setSplashDismissed(true); }}
                            className="mt-2 inline-flex items-center gap-2 text-white bg-gradient-to-r from-flamingo-500 to-flamingo-600 hover:from-flamingo-600 hover:to-flamingo-700 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow hover:shadow-lg hover:-translate-y-px"
                        >
                            <Plus size={16} /> Start adding rows
                        </button>
                    </div>
                </div>
            )}

            {!isEmptySurface && unitAreas.map((area, areaIdx) => {
                const accent = AREA_ACCENT;
                const totalRows = area.workItems.reduce((acc, wi) => acc + wi.items.length, 0);
                const updateRowProp = makeUpdateItem(areaIdx);
                const deleteRowProp = makeDeleteItem(areaIdx);
                return (
                    <div key={area.id} className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-all hover:shadow-md">
                        <div className={`relative flex items-center gap-3 px-4 py-3.5 bg-gradient-to-r ${accent.gradient}`}>
                            <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:10px_10px] pointer-events-none" />
                            <button type="button" onClick={() => toggleAreaCollapse(areaIdx)}
                                className="relative flex items-center justify-center w-7 h-7 rounded-md hover:bg-white/20 text-white/80 hover:text-white transition-colors shrink-0"
                                aria-label={area.isCollapsed ? 'Expand' : 'Collapse'}>
                                {area.isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                            </button>
                            <div className={`relative w-9 h-9 rounded-xl ${accent.medallionBg} border ${accent.medallionBorder} flex items-center justify-center shrink-0 shadow-inner`}>
                                <span className={`${accent.medallionText} text-sm font-bold tabular-nums`}>{areaIdx + 1}</span>
                            </div>
                            <div className="relative flex-1 min-w-0 max-w-[340px]">
                                <p className={`text-[9px] uppercase tracking-[0.16em] ${accent.eyebrow} font-semibold leading-none mb-1`}>Unit area</p>
                                <input type="text" list={`area-name-suggestions-${area.id}`}
                                    className="w-full px-3 py-1.5 text-sm font-semibold text-white bg-white/10 border border-white/20 rounded-lg outline-none focus:border-white/50 focus:bg-white/20 focus:ring-1 focus:ring-white/30 placeholder:text-white/50 transition-colors"
                                    value={area.areaName} onChange={(e) => updateAreaName(areaIdx, e.target.value)}
                                    placeholder="e.g. Hall, Kitchen, Master Bedroom…" />
                                <datalist id={`area-name-suggestions-${area.id}`}>
                                    {areaNameOptions.map((opt) => <option key={opt.value} value={opt.value} />)}
                                </datalist>
                            </div>
                            <span className="relative inline-flex items-center gap-1.5 text-[11px] font-medium text-white/90 px-2.5 py-1 bg-white/10 rounded-full border border-white/15 tabular-nums">
                                <Wrench size={11} className="opacity-80" />{area.workItems.length}
                                <span className="text-white/40">·</span>
                                <Boxes size={11} className="opacity-80" />{totalRows}
                            </span>
                            <div className="flex-1" />
                            <button type="button" onClick={() => deleteUnitArea(areaIdx)}
                                className="relative flex items-center justify-center w-8 h-8 rounded-md text-white/60 hover:text-rose-200 hover:bg-rose-500/20 transition-colors" title="Remove unit area">
                                <Trash2 size={14} />
                            </button>
                        </div>

                        {!area.isCollapsed && (
                            <div className="p-4 flex flex-col gap-3 bg-slate-50/30">
                                {area.workItems.map((wi, wiIdx) => (
                                    <div key={wi.id} className={`rounded-xl border ${accent.chipBorder} bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow`}>
                                        <div className={`flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-gradient-to-r ${accent.chipBg} via-white to-white`}>
                                            <button type="button" onClick={() => toggleWorkItemCollapse(areaIdx, wiIdx)}
                                                className="flex items-center justify-center w-6 h-6 rounded hover:bg-flamingo-100/60 text-slate-500 hover:text-flamingo-700 transition-colors shrink-0"
                                                aria-label={wi.isCollapsed ? 'Expand' : 'Collapse'}>
                                                {wi.isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white border border-slate-200 text-[11px] font-semibold text-slate-600 tabular-nums shrink-0">{wiIdx + 1}</span>
                                            <div className="flex-1 min-w-0 max-w-[320px]">
                                                <p className={`text-[9px] uppercase tracking-[0.14em] ${accent.chipText} font-semibold leading-none mb-1`}>Work item</p>
                                                <input type="text" list={`work-item-suggestions-${wi.id}`}
                                                    className="w-full px-2.5 py-1.5 text-sm font-medium text-slate-800 bg-white border border-slate-200 rounded-md outline-none focus:border-flamingo-400 focus:ring-1 focus:ring-flamingo-300/40 placeholder:text-slate-400"
                                                    value={wi.workItemName} onChange={(e) => updateWorkItemName(areaIdx, wiIdx, e.target.value)}
                                                    placeholder="e.g. TV Unit, False Ceiling, Wardrobe…" />
                                                <datalist id={`work-item-suggestions-${wi.id}`}>
                                                    {WORK_ITEM_SUGGESTIONS.map((s) => <option key={s} value={s} />)}
                                                </datalist>
                                            </div>
                                            <span className="text-[11px] font-medium text-slate-600 px-2 py-0.5 bg-white rounded-full border border-slate-200 tabular-nums">
                                                {wi.items.length} {wi.items.length === 1 ? 'row' : 'rows'}
                                            </span>
                                            <div className="flex-1" />
                                            <button type="button" onClick={() => deleteWorkItem(areaIdx, wiIdx)}
                                                className="flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Remove work item">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>

                                        {!wi.isCollapsed && (
                                            <div className="bg-white">
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-left border-collapse text-sm">
                                                        <thead>
                                                            <tr className="bg-slate-100/70 text-slate-600 border-b border-slate-200">
                                                                <th className="py-2.5 px-3 font-semibold text-[10px] uppercase tracking-[0.08em] text-left">Material</th>
                                                                <th className="py-2.5 px-3 w-[110px] text-right font-semibold text-[10px] uppercase tracking-[0.08em]">Qty</th>
                                                                <th className="py-2.5 px-3 w-[140px] font-semibold text-[10px] uppercase tracking-[0.08em] text-left">Unit</th>
                                                                <th className="py-2.5 px-3 w-[220px] font-semibold text-[10px] uppercase tracking-[0.08em] text-left">Notes</th>
                                                                <th className="py-2 px-1.5 w-[40px] min-w-[40px] text-center"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {wi.items.map((item, itemIdx) => (
                                                                <BoqRowGrouped
                                                                    key={item.id}
                                                                    row={item}
                                                                    index={itemIdx}
                                                                    sectionIndex={wiIdx}
                                                                    updateRow={updateRowProp}
                                                                    deleteRow={deleteRowProp}
                                                                />
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div className="px-3 py-2 border-t border-slate-100 bg-white">
                                                    <button type="button" onClick={() => addMaterialRow(areaIdx, wiIdx)}
                                                        className={`inline-flex items-center gap-1.5 ${accent.chipText} px-3 py-1.5 rounded-md text-xs font-semibold transition-all hover:gap-2`}>
                                                        <Plus size={13} /> Add material row
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <div className="pt-1">
                                    <button type="button" onClick={() => addWorkItem(areaIdx)}
                                        className={`group inline-flex items-center gap-1.5 ${accent.chipText} ${accent.chipBg} border border-dashed ${accent.chipBorder} px-3.5 py-2 rounded-lg text-xs font-semibold transition-all hover:gap-2 hover:shadow-sm`}>
                                        <Plus size={14} /> Add work item
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}

            {!isEmptySurface && (
                <div className="flex justify-center pt-2 pb-1">
                    <button type="button" onClick={addUnitArea}
                        className="group inline-flex items-center gap-2 text-white bg-gradient-to-r from-flamingo-500 to-flamingo-600 hover:from-flamingo-600 hover:to-flamingo-700 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm hover:shadow-lg hover:-translate-y-px focus:outline-none focus:ring-2 focus:ring-flamingo-300/60">
                        <Plus size={16} className="transition-transform group-hover:rotate-90" />
                        Add unit area
                    </button>
                </div>
            )}
        </div>
    );
};

export default AreaWorkItemBoqTable;
